/**
 * ReportChrome — shared header / footer / section-bar + export helpers for the
 * printable/exported Team Roster report (PrintView + the Excel export).
 * Centralizing this keeps the header, footer, "current view" filter display,
 * and filename rules uniform — matching the Equipment Temperatures report
 * (store-equipment/report-print-view.tsx). No divider rules anywhere
 * (spacing only).
 */
import React from 'react';
import { OpsbeeWordmarkIcon } from '@pimm/base';

// ─── shared print CSS (header / footer / section bar) ────────────────────────
// Injected once per print view alongside its own paper styles. No border rules —
// header/footer separate from the body by spacing only.
export const REPORT_CHROME_STYLES = `
  .rpt-hdr {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    margin-bottom: 12px;
    flex-shrink: 0;
    font-family: 'Inter', sans-serif;
  }
  .rpt-hdr-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .rpt-hdr-logo { height: 34px; max-width: 120px; object-fit: contain; }
  .rpt-hdr-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    line-height: 1.1;
  }
  .rpt-hdr-sub { font-size: 11px; color: #4b5563; font-weight: 500; margin-top: 2px; }
  .rpt-hdr-right { text-align: right; flex-shrink: 0; padding-left: 16px; }
  .rpt-hdr-right-line { font-size: 11px; color: #4b5563; font-weight: 600; line-height: 1.5; }

  .rpt-ftr {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-top: 8px;
    margin-top: auto;
    flex-shrink: 0;
    font-family: 'Inter', sans-serif;
    font-size: 9px;
    color: #6b7280;
  }
  .rpt-ftr-center { text-align: center; }
  .rpt-ftr-right { text-align: right; }

  .rpt-section-bar {
    background: #f1f3f5;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    border-radius: 3px;
    padding: 5px 10px;
    margin-bottom: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #111827;
  }

  /* Store Information section — mirrors the Live Status equipment print
     report's equipment-info grid (store-equipment/report-print-view.tsx). */
  .rpt-store-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 18px;
    row-gap: 8px;
    font-family: 'Inter', sans-serif;
    margin-bottom: 2px;
    /* Matches .rpt-section-bar's 10px left padding above it — cells would
       otherwise start flush left at 0, reading as misaligned under the
       indented "Store Information" bar label. */
    padding: 0 10px;
  }
  .rpt-store-cell-label {
    font-size: 8px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin-bottom: 1px;
  }
  .rpt-store-cell-value {
    font-size: 10px;
    font-weight: 600;
    color: #111827;
    line-height: 1.3;
  }
`;

// ─── header ──────────────────────────────────────────────────────────────────
// Left: brand logo + uppercase title + optional subtitle.
// Right: stacked lines — the "current view" context (filters) + site name.
export const ReportHeader: React.FC<{
  logoUrl?: string;
  title: string;
  subtitle?: string;
  rightLines: string[];
}> = ({ logoUrl, title, subtitle, rightLines }) => (
  <div className="rpt-hdr">
    <div className="rpt-hdr-left">
      {logoUrl ? <img src={logoUrl} className="rpt-hdr-logo" alt="" /> : null}
      <div>
        <div className="rpt-hdr-title">{title}</div>
        {subtitle ? <div className="rpt-hdr-sub">{subtitle}</div> : null}
      </div>
    </div>
    <div className="rpt-hdr-right">
      {rightLines.filter(Boolean).map((line, i) => (
        <div key={i} className="rpt-hdr-right-line">
          {line}
        </div>
      ))}
    </div>
  </div>
);

// ─── footer ────────────────────────────────────────────────────────────────
// Left: opsbee wordmark. Center: "Generated at {date} · {by}" — single line.
export const ReportFooter: React.FC<{
  centerText: string;
  pageLabel: string;
}> = ({ centerText, pageLabel }) => (
  <div className="rpt-ftr">
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <OpsbeeWordmarkIcon height={16} />
    </span>
    <span className="rpt-ftr-center">{centerText}</span>
    <span className="rpt-ftr-right">{pageLabel}</span>
  </div>
);

// ─── filled section title (replaces underlined headings) ─────────────────────
export const ReportSectionBar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rpt-section-bar">{children}</div>
);

// ─── Store Information section ────────────────────────────────────────────
// Customer / Store / Address only — a single row of 3.
export type ReportStoreInfoProps = {
  customerLabel: string;
  customerValue: string;
  storeLabel: string;
  storeValue: string;
  addressLabel: string;
  addressValue: string;
};

const StoreInfoCell: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div>
    <div className="rpt-store-cell-label">{label}</div>
    <div className="rpt-store-cell-value">{value || '—'}</div>
  </div>
);

export const ReportStoreInfo: React.FC<ReportStoreInfoProps> = ({
  customerLabel,
  customerValue,
  storeLabel,
  storeValue,
  addressLabel,
  addressValue,
}) => (
  <div className="rpt-store-grid">
    <StoreInfoCell label={customerLabel} value={customerValue} />
    <StoreInfoCell label={storeLabel} value={storeValue} />
    <StoreInfoCell label={addressLabel} value={addressValue} />
  </div>
);

// ─── filename rules ──────────────────────────────────────────────────────────
// {Store}_{Report}_{YYYY-MM-DD}. Mirrors store-equipment's buildExportFileName:
// strips filesystem-illegal chars, collapses whitespace, safe fallbacks.
const sanitizeFilePart = (s: string | undefined, fallback: string): string => {
  const cleaned = (s ?? '')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || fallback;
};

const ymd = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// `filterType` — the active status filter (Active, Not Active, Issues:
// <kind>, or "Full Report" for the unfiltered scope), so the filename itself
// tells you what's in the file without opening it.
export const buildExportFileName = (
  store: string | undefined,
  reportType: string | undefined,
  generatedDate: Date = new Date(),
  filterType?: string,
): string =>
  [
    sanitizeFilePart(store, 'site'),
    sanitizeFilePart(reportType, 'report'),
    ...(filterType ? [sanitizeFilePart(filterType, '')] : []),
    ymd(generatedDate),
  ]
    .filter(Boolean)
    .join('_');

// ─── print filename ──────────────────────────────────────────────────────────
// Browsers use `document.title` at the moment window.print() fires as the
// suggested filename in the "Save as PDF" dialog — there's no other hook for
// this. Temporarily rename the document, print, then restore the original
// title on `afterprint` so the tab title elsewhere in the app is unaffected.
// Same {Store}_{Report}_{YYYY-MM-DD} convention as the Excel export.
export const printWithFileName = (fileName: string, doPrint: () => void): void => {
  const originalTitle = document.title;
  document.title = fileName;
  const restore = () => {
    window.removeEventListener('afterprint', restore);
    document.title = originalTitle;
  };
  window.addEventListener('afterprint', restore);
  doPrint();
};

// ─── address formatting ────────────────────────────────────────────────────
// Matches `@pimm/services/lib/sms-tenants`'s real `Address` shape (address1/city/state/postal).
export const formatReportAddress = (address?: { address1?: string; city?: string; state?: string; postal?: string }): string =>
  address ? [address.address1, address.city, [address.state, address.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '';

// ─── Excel info sheet ────────────────────────────────────────────────────────
// A Label/Value metadata sheet that carries the same context as the print
// header/footer. Mirrors store-equipment's buildEquipmentInfoRows.
export type ReportInfoRow = { Label: string; Value: string };

export type ReportInfoInput = {
  store?: string;
  view?: string; // "Employees" / "opsbee Users"
  filters?: string; // the current-view filter description (or "Full report")
  rows?: number; // row count in the export
  generatedBy?: string;
  generatedAt: string;
};

export type ReportInfoLabels = {
  store: string;
  view: string;
  filters: string;
  rows: string;
  generatedBy: string;
  generatedAt: string;
};

// Labels ship directly into the exported .xlsx file, so the caller passes
// them in (via `t()`) rather than this helper hardcoding English.
export const buildReportInfoRows = (input: ReportInfoInput, labels: ReportInfoLabels): ReportInfoRow[] => {
  const rows: ReportInfoRow[] = [];
  const push = (label: string, value: string | number | undefined | null) =>
    rows.push({ Label: label, Value: value == null || value === '' ? '—' : String(value) });

  push(labels.store, input.store);
  push(labels.view, input.view);
  push(labels.filters, input.filters);
  if (typeof input.rows === 'number') push(labels.rows, input.rows);
  push(labels.generatedBy, input.generatedBy);
  push(labels.generatedAt, input.generatedAt);

  return rows;
};
